import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Typography,
  Chip,
  Paper,
  Collapse,
  Button,
  IconButton,
  Menu,
  MenuItem,
} from "@mui/material";
import BarrierIcon from "@mui/icons-material/Block";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import DeleteIcon from "@mui/icons-material/Delete";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import GlassCard from "../../../components/common/GlassCard";

function BarriersCard({ barriers, onAddBarrier, onDeleteBarrier }) {
  const [expandAll, setExpandAll] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [activeId, setActiveId] = useState(null);

  const closeMenu = () => {
    setMenuAnchor(null);
    setActiveId(null);
  };

  const renderMenuButton = (barrier) =>
    onDeleteBarrier && (
      <IconButton
        size="small"
        aria-label="Barrier options"
        onClick={(e) => {
          setMenuAnchor(e.currentTarget);
          setActiveId(barrier.id);
        }}
      >
        <MoreVertIcon fontSize="small" />
      </IconButton>
    );

  const sortedBarriers = barriers
    ? [...barriers].sort((a, b) => {
        const dateA = new Date(a.identified_date || 0);
        const dateB = new Date(b.identified_date || 0);
        return dateB - dateA;
      })
    : [];
  return (
    <GlassCard
      title="Barriers to Care"
      addLabel="Add Barrier"
      icon={<BarrierIcon color="warning" />}
      onAdd={onAddBarrier}
    >
      {barriers?.length ? (
        <>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 2 }}>
            {barriers.slice(0, 3).map((barrier) => (
              <Paper
                key={barrier.id}
                sx={{
                  p: 1.25,
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1,
                }}
              >
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={0.5}>
                  <Box flex={1}>
                    <Typography variant="body2" fontWeight={600}>
                      {barrier.barrier_type ? barrier.barrier_type.replace("_", " ") : "Barrier"}
                    </Typography>
                  </Box>
                  <Box display="flex" gap={0.5} alignItems="center">
                    <Chip
                      label={barrier.status || "unknown"}
                      size="small"
                      color={barrier.status === "resolved" ? "success" : "warning"}
                      variant="outlined"
                    />
                    {renderMenuButton(barrier)}
                  </Box>
                </Box>
                {barrier.description && (
                  <Typography variant="caption" color="text.secondary" display="block">
                    {barrier.description.substring(0, 100)}{barrier.description.length > 100 ? "..." : ""}
                  </Typography>
                )}
                {barrier.identified_date && (
                  <Typography variant="caption" color="text.secondary" display="block">
                    {new Date(barrier.identified_date).toLocaleDateString()}
                  </Typography>
                )}
              </Paper>
            ))}
          </Box>

          {sortedBarriers?.length > 2 && (
            <>
              <Collapse in={expandAll} timeout="auto" unmountOnExit>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 2 }}>
                  {barriers.slice(3).map((barrier) => (
                    <Paper
                      key={barrier.id}
                      sx={{
                        p: 1.25,
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 1,
                      }}
                    >
                      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={0.5}>
                        <Box flex={1}>
                          <Typography variant="body2" fontWeight={600}>
                            {barrier.barrier_type ? barrier.barrier_type.replace("_", " ") : "Barrier"}
                          </Typography>
                        </Box>
                        <Box display="flex" gap={0.5} alignItems="center">
                          <Chip
                            label={barrier.status || "unknown"}
                            size="small"
                            color={barrier.status === "resolved" ? "success" : "warning"}
                            variant="outlined"
                          />
                          {renderMenuButton(barrier)}
                        </Box>
                      </Box>
                      {barrier.description && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {barrier.description.substring(0, 100)}{barrier.description.length > 100 ? "..." : ""}
                        </Typography>
                      )}
                      {barrier.identified_date && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {new Date(barrier.identified_date).toLocaleDateString()}
                        </Typography>
                      )}
                    </Paper>
                  ))}
                </Box>
              </Collapse>

              <Button
                size="small"
                onClick={() => setExpandAll(!expandAll)}
                startIcon={expandAll ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                sx={{ textTransform: "none", mt: 1 }}
              >
                {expandAll ? "View Less" : `View All (${sortedBarriers.length - 2} more)`}
              </Button>
            </>
          )}
        </>
      ) : (
        <Typography variant="body2" color="text.secondary">
          No barriers identified
        </Typography>
      )}
      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={closeMenu}>
        <MenuItem
          onClick={() => {
            const id = activeId;
            closeMenu();
            if (id && onDeleteBarrier) onDeleteBarrier(id);
          }}
        >
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} /> Delete
        </MenuItem>
      </Menu>
    </GlassCard>
  );
}

BarriersCard.propTypes = {
  barriers: PropTypes.array,
  onAddBarrier: PropTypes.func.isRequired,
  onDeleteBarrier: PropTypes.func,
};

BarriersCard.defaultProps = {
  barriers: [],
};

export default BarriersCard;
